import { useState, useEffect } from "react";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Button,
  Badge,
  Input,
  Label,
} from "@/components/ui";
import { PageHeader } from "@/components/ui/page-header";
import { ConfirmDialog } from "@/components/ui/confirm-dialog";
import { useAuthStore } from "@/stores/auth.store";
import {
  Key,
  Plus,
  Copy,
  Trash2,
  Loader2,
  Eye,
  Clock,
  Shield,
  AlertTriangle,
  CheckCircle,
  X,
} from "lucide-react";
import { companyApi } from "@/services/api";

interface ApiKey {
  id: string;
  keyPrefix: string;
  name: string;
  description?: string;
  status: "ACTIVE" | "REVOKED" | "EXPIRED";
  expiresAt?: string;
  rateLimitPerMinute: number;
  rateLimitPerHour: number;
  ipWhitelist: string[];
  lastUsedAt?: string;
  usageCount: number;
  createdAt: string;
}

interface ApiKeyCreated extends ApiKey {
  key: string;
  warning: string;
}

export function CompanyApiKeysPage() {
  const user = useAuthStore((state) => state.user);
  const isAdmin = user?.role === "ADMIN";

  const [apiKeys, setApiKeys] = useState<ApiKey[]>([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showCreatedKeyDialog, setShowCreatedKeyDialog] = useState(false);
  const [createdKey, setCreatedKey] = useState<ApiKeyCreated | null>(null);
  const [showRevokeConfirm, setShowRevokeConfirm] = useState(false);
  const [keyToRevoke, setKeyToRevoke] = useState<ApiKey | null>(null);
  const [revoking, setRevoking] = useState(false);

  // Create form state
  const [createForm, setCreateForm] = useState({
    name: "",
    description: "",
    expiresAt: "",
    ipWhitelist: "",
  });
  const [creating, setCreating] = useState(false);
  const [createError, setCreateError] = useState("");

  // Copy state
  const [copiedKey, setCopiedKey] = useState(false);

  const loadApiKeys = async () => {
    setLoading(true);
    try {
      const data = await companyApi.apiKeys.list();
      setApiKeys(data);
    } catch (error) {
      console.error("Erro ao carregar API keys:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadApiKeys();
  }, []);

  const handleCreate = async () => {
    setCreateError("");
    setCreating(true);

    try {
      const data = await companyApi.apiKeys.create({
        name: createForm.name,
        description: createForm.description || undefined,
        expiresAt: createForm.expiresAt || undefined,
        ipWhitelist: createForm.ipWhitelist
          ? createForm.ipWhitelist.split(",").map((ip) => ip.trim())
          : undefined,
      });

      setCreatedKey(data);
      setShowCreateModal(false);
      setShowCreatedKeyDialog(true);
      setCreateForm({ name: "", description: "", expiresAt: "", ipWhitelist: "" });
      loadApiKeys();
    } catch (error: any) {
      setCreateError(error.message || "Erro ao criar API key");
    } finally {
      setCreating(false);
    }
  };

  const handleRevoke = async () => {
    if (!keyToRevoke) return;

    setRevoking(true);
    try {
      await companyApi.apiKeys.revoke(keyToRevoke.id);
      setShowRevokeConfirm(false);
      setKeyToRevoke(null);
      loadApiKeys();
    } catch (error) {
      console.error("Erro ao revogar API key:", error);
    } finally {
      setRevoking(false);
    }
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
    setCopiedKey(true);
    setTimeout(() => setCopiedKey(false), 2000);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString("pt-BR", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case "ACTIVE":
        return <Badge className="bg-green-500">Ativa</Badge>;
      case "REVOKED":
        return <Badge className="bg-red-500">Revogada</Badge>;
      case "EXPIRED":
        return <Badge className="bg-yellow-500">Expirada</Badge>;
      default:
        return <Badge>{status}</Badge>;
    }
  };

  const activeKeys = apiKeys.filter((k) => k.status === "ACTIVE").length;

  return (
    <div className="p-4 sm:p-6 space-y-4 sm:space-y-6">
      <PageHeader
        icon={<Key className="w-5 h-5 sm:w-6 sm:h-6" />}
        title="API Keys"
        description="Gerencie suas chaves de acesso"
        stats={[
          { value: apiKeys.length.toString(), label: "Total de keys" },
          { value: activeKeys.toString(), label: "Keys ativas" },
        ]}
        actions={
          isAdmin && (
            <Button
              className="bg-white/20 hover:bg-white/30 text-white border-0"
              onClick={() => setShowCreateModal(true)}
            >
              <Plus className="w-4 h-4 mr-2" />
              Nova API Key
            </Button>
          )
        }
      />

      {/* Info Card */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base">
            <Shield className="w-5 h-5 text-primary" />
            Sobre a API Externa
          </CardTitle>
        </CardHeader>
        <CardContent className="text-sm text-muted-foreground space-y-2">
          <p>
            A API externa permite que sistemas integrados consultem dados da sua conta
            programaticamente, como saldo, rendimentos e histórico de operações.
          </p>
          <p>
            <strong>Documentação:</strong>{" "}
            <a
              href="/api-docs"
              className="text-primary hover:underline"
            >
              Abrir documentação da API
            </a>
          </p>
          <p>
            <strong>Rate Limit padrão:</strong> 60 requisições/minuto, 1000 requisições/hora
          </p>
        </CardContent>
      </Card>

      {/* API Keys List */}
      <Card>
        <CardHeader>
          <CardTitle>Suas API Keys</CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex justify-center py-8">
              <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
            </div>
          ) : apiKeys.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              <Key className="w-12 h-12 mx-auto mb-4 opacity-50" />
              <p>Nenhuma API key criada ainda.</p>
              {isAdmin && (
                <Button
                  variant="outline"
                  className="mt-4"
                  onClick={() => setShowCreateModal(true)}
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Criar primeira API Key
                </Button>
              )}
            </div>
          ) : (
            <div className="space-y-3 sm:space-y-4">
              {apiKeys.map((key) => (
                <div
                  key={key.id}
                  className="flex flex-col sm:flex-row sm:items-center justify-between p-3 sm:p-4 border rounded-lg gap-3"
                >
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1 flex-wrap">
                      <span className="font-medium">{key.name}</span>
                      {getStatusBadge(key.status)}
                    </div>
                    <div className="text-sm text-muted-foreground space-y-1">
                      <p className="font-mono text-xs sm:text-sm truncate">{key.keyPrefix}...</p>
                      {key.description && <p className="text-xs sm:text-sm">{key.description}</p>}
                      <div className="flex flex-wrap gap-2 sm:gap-4 mt-2 text-xs sm:text-sm">
                        <span className="flex items-center gap-1">
                          <Clock className="w-3 h-3" />
                          <span className="hidden sm:inline">Criada em</span> {formatDate(key.createdAt)}
                        </span>
                        {key.lastUsedAt && (
                          <span className="flex items-center gap-1">
                            <Eye className="w-3 h-3" />
                            <span className="hidden sm:inline">Último uso:</span> {formatDate(key.lastUsedAt)}
                          </span>
                        )}
                        <span>{key.usageCount} req.</span>
                      </div>
                      {key.expiresAt && (
                        <p className="text-yellow-600 flex items-center gap-1 text-xs sm:text-sm">
                          <AlertTriangle className="w-3 h-3" />
                          Expira em {formatDate(key.expiresAt)}
                        </p>
                      )}
                      {key.ipWhitelist.length > 0 && (
                        <p className="text-xs truncate">
                          IPs: {key.ipWhitelist.join(", ")}
                        </p>
                      )}
                    </div>
                  </div>
                  {isAdmin && key.status === "ACTIVE" && (
                    <Button
                      variant="outline"
                      size="sm"
                      className="text-red-500 hover:text-red-600 hover:bg-red-50 self-start sm:self-center flex-shrink-0"
                      onClick={() => {
                        setKeyToRevoke(key);
                        setShowRevokeConfirm(true);
                      }}
                    >
                      <Trash2 className="w-4 h-4 sm:mr-1" />
                      <span className="hidden sm:inline">Revogar</span>
                    </Button>
                  )}
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Create Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <Card className="w-full max-w-md mx-4">
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle>Criar Nova API Key</CardTitle>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setShowCreateModal(false)}
                disabled={creating}
              >
                <X className="w-4 h-4" />
              </Button>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="name">Nome *</Label>
                <Input
                  id="name"
                  placeholder="Ex: Produção, Homologação"
                  value={createForm.name}
                  onChange={(e) =>
                    setCreateForm({ ...createForm, name: e.target.value })
                  }
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="description">Descrição</Label>
                <Input
                  id="description"
                  placeholder="Descrição opcional"
                  value={createForm.description}
                  onChange={(e) =>
                    setCreateForm({ ...createForm, description: e.target.value })
                  }
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="expiresAt">Data de Expiração (opcional)</Label>
                <Input
                  id="expiresAt"
                  type="date"
                  value={createForm.expiresAt}
                  onChange={(e) =>
                    setCreateForm({ ...createForm, expiresAt: e.target.value })
                  }
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="ipWhitelist">IPs Permitidos (opcional)</Label>
                <Input
                  id="ipWhitelist"
                  placeholder="192.168.1.1, 10.0.0.1"
                  value={createForm.ipWhitelist}
                  onChange={(e) =>
                    setCreateForm({ ...createForm, ipWhitelist: e.target.value })
                  }
                />
                <p className="text-xs text-muted-foreground">
                  Separe múltiplos IPs por vírgula. Deixe em branco para permitir
                  qualquer IP.
                </p>
              </div>

              {createError && (
                <div className="p-3 text-sm text-destructive bg-destructive/10 rounded-md">
                  {createError}
                </div>
              )}

              <div className="flex justify-end gap-2 pt-4">
                <Button
                  variant="outline"
                  onClick={() => setShowCreateModal(false)}
                  disabled={creating}
                >
                  Cancelar
                </Button>
                <Button
                  onClick={handleCreate}
                  disabled={!createForm.name || creating}
                >
                  {creating ? (
                    <Loader2 className="w-4 h-4 animate-spin mr-2" />
                  ) : (
                    <Plus className="w-4 h-4 mr-2" />
                  )}
                  Criar
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Created Key Dialog */}
      {showCreatedKeyDialog && createdKey && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <Card className="w-full max-w-lg mx-4">
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="flex items-center gap-2 text-green-600">
                <CheckCircle className="w-5 h-5" />
                API Key Criada!
              </CardTitle>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setShowCreatedKeyDialog(false)}
              >
                <X className="w-4 h-4" />
              </Button>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                <div className="flex items-start gap-2">
                  <AlertTriangle className="w-5 h-5 text-yellow-600 flex-shrink-0 mt-0.5" />
                  <div className="text-sm text-yellow-800">
                    <p className="font-medium">Importante!</p>
                    <p>
                      Esta é a única vez que a key completa será exibida. Salve-a em
                      um local seguro.
                    </p>
                  </div>
                </div>
              </div>

              <div className="space-y-2">
                <Label>Sua API Key</Label>
                <div className="flex gap-2">
                  <Input
                    readOnly
                    value={createdKey.key}
                    className="font-mono text-sm"
                  />
                  <Button
                    variant="outline"
                    onClick={() => copyToClipboard(createdKey.key)}
                  >
                    {copiedKey ? (
                      <CheckCircle className="w-4 h-4 text-green-500" />
                    ) : (
                      <Copy className="w-4 h-4" />
                    )}
                  </Button>
                </div>
              </div>

              <div className="text-sm text-muted-foreground">
                <p>
                  <strong>Nome:</strong> {createdKey.name}
                </p>
                <p>
                  <strong>Rate Limit:</strong> {createdKey.rateLimitPerMinute}/min,{" "}
                  {createdKey.rateLimitPerHour}/hora
                </p>
              </div>

              <div className="flex justify-end pt-4">
                <Button onClick={() => setShowCreatedKeyDialog(false)}>
                  Entendi, salvei a key
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Revoke Confirm Dialog */}
      <ConfirmDialog
        open={showRevokeConfirm}
        title="Revogar API Key?"
        description={`Tem certeza que deseja revogar a API key "${keyToRevoke?.name}"? Esta ação não pode ser desfeita e todas as integrações usando esta key deixarão de funcionar.`}
        confirmText="Revogar"
        variant="danger"
        loading={revoking}
        onConfirm={handleRevoke}
        onClose={() => {
          setShowRevokeConfirm(false);
          setKeyToRevoke(null);
        }}
      />
    </div>
  );
}
